import { supabase } from '../lib/supabase';

/**
 * Wallet Service - Data layer for managing user Bitcoin wallet addresses
 * 
 * This service provides CRUD operations for the wallets table in Supabase.
 * All operations are automatically scoped to the authenticated user via RLS policies.
 * 
 * Database Schema:
 * - id: UUID primary key
 * - user_id: UUID foreign key to auth.users
 * - created_at, updated_at: Timestamps
 * - wallet_address: TEXT - Bitcoin address
 * - wallet_name: TEXT - User-defined name for the wallet
 * - balance: DECIMAL - Current balance in BTC (optional, can be updated separately)
 * - last_checked: TIMESTAMP - Last time balance was checked (optional)
 */

/**
 * Creates a new Bitcoin wallet tracking record for the authenticated user
 * 
 * @param {Object} walletData - The wallet data to save
 * @param {string} walletData.address - Bitcoin wallet address (required)
 * @param {string} walletData.wallet_name - User-friendly name for the wallet (required)
 * @param {number} [walletData.balance] - Current balance in BTC (optional)
 * @param {string} [walletData.last_checked] - ISO timestamp of last balance check (optional)
 * 
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 *   - data: The created wallet object if successful
 *   - error: Error object if operation failed
 */
async function createWallet(walletData) {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session || !session.user) {
      return { data: null, error: new Error('User not authenticated. Please log in to track wallets.') };
    }

    // Validate required fields
    if (!walletData.address || walletData.address.trim() === '') {
      return { data: null, error: new Error('Wallet address is required') };
    }
    
    if (!walletData.wallet_name || walletData.wallet_name.trim() === '') {
      return { data: null, error: new Error('Wallet name is required') };
    }

    // Check if address already exists for this user
    const { data: existingWallet } = await supabase
      .from('wallets')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('wallet_address', walletData.address.trim())
      .single();

    if (existingWallet) {
      return { data: null, error: new Error('This wallet address is already being tracked') };
    }

    // Prepare the wallet record
    const walletRecord = {
      user_id: session.user.id,
      wallet_address: walletData.address.trim(),
      wallet_name: walletData.wallet_name.trim(),
      balance: walletData.balance || null,
      last_checked: walletData.last_checked || null
    };

    // Insert the wallet into the database
    const { data, error } = await supabase
      .from('wallets')
      .insert([walletRecord])
      .select('*')
      .single();

    if (error) {
      console.error('Database error creating wallet:', error);
      return { data: null, error: new Error(`Failed to create wallet: ${error.message}`) };
    }

    return { data, error: null };

  } catch (error) {
    console.error('Unexpected error in createWallet:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Retrieves all tracked wallets for the authenticated user
 * 
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 *   - data: Array of wallet objects if successful, empty array if no wallets
 *   - error: Error object if operation failed
 */
async function getWallets() {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session || !session.user) {
      return { data: null, error: new Error('User not authenticated. Please log in to view wallets.') };
    }

    // Query wallets for the current user
    const { data, error } = await supabase
      .from('wallets')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Database error fetching wallets:', error);
      return { data: null, error: new Error(`Failed to fetch wallets: ${error.message}`) };
    }

    // Return wallets (data will be an empty array if no wallets exist)
    return { data: data || [], error: null };

  } catch (error) {
    console.error('Unexpected error in getWallets:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Updates an existing wallet by ID
 * 
 * @param {string} walletId - The UUID of the wallet to update
 * @param {Object} updates - Object containing fields to update
 * @param {string} [updates.wallet_name] - Updated wallet name
 * @param {number} [updates.balance] - Updated balance in BTC
 * @param {string} [updates.last_checked] - Updated last check timestamp
 * 
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 *   - data: The updated wallet object if successful
 *   - error: Error object if operation failed
 */
async function updateWallet(walletId, updates) {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session || !session.user) {
      return { data: null, error: new Error('User not authenticated. Please log in to update wallets.') };
    }

    // Validate walletId
    if (!walletId || typeof walletId !== 'string') {
      return { data: null, error: new Error('Valid wallet ID is required') };
    }

    // Validate updates object
    if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
      return { data: null, error: new Error('Updates object is required and must not be empty') };
    }

    // Add updated_at timestamp
    const finalUpdates = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    // Update the wallet
    const { data, error } = await supabase
      .from('wallets')
      .update(finalUpdates)
      .eq('id', walletId)
      .eq('user_id', session.user.id) // Extra security: ensure user owns this wallet
      .select('*')
      .single();

    if (error) {
      console.error('Database error updating wallet:', error);
      
      // Provide more specific error messages
      if (error.code === 'PGRST116') {
        return { data: null, error: new Error('Wallet not found or you do not have permission to update it') };
      }
      
      return { data: null, error: new Error(`Failed to update wallet: ${error.message}`) };
    }

    return { data, error: null };

  } catch (error) {
    console.error('Unexpected error in updateWallet:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Deletes a wallet by ID
 * 
 * @param {string} walletId - The UUID of the wallet to delete
 * 
 * @returns {Promise<{data: boolean|null, error: Error|null}>}
 *   - data: true if deletion was successful
 *   - error: Error object if operation failed
 */
async function deleteWallet(walletId) {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session || !session.user) {
      return { data: null, error: new Error('User not authenticated. Please log in to delete wallets.') };
    }

    // Validate walletId
    if (!walletId || typeof walletId !== 'string') {
      return { data: null, error: new Error('Valid wallet ID is required') };
    }

    // Delete the wallet
    const { error } = await supabase
      .from('wallets')
      .delete()
      .eq('id', walletId)
      .eq('user_id', session.user.id); // Extra security: ensure user owns this wallet

    if (error) {
      console.error('Database error deleting wallet:', error);
      
      // Provide more specific error messages
      if (error.code === 'PGRST116') {
        return { data: null, error: new Error('Wallet not found or you do not have permission to delete it') };
      }
      
      return { data: null, error: new Error(`Failed to delete wallet: ${error.message}`) };
    }

    return { data: true, error: null };

  } catch (error) {
    console.error('Unexpected error in deleteWallet:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Wallet Service Object
 * 
 * Provides a clean interface for all wallet-related database operations.
 * All methods handle authentication, validation, and error handling automatically.
 */
const walletService = {
  createWallet,
  getWallets,
  updateWallet,
  deleteWallet
};

export default walletService;